import * as Sentry from "@sentry/react";
import { env } from "@/env";
import type { UpdateState } from "@/lib/pwa/serviceWorkerClient";

/**
 * Initialize Sentry error tracking and performance monitoring.
 * Must be called before React renders, typically in main.tsx.
 *
 * @param router - TanStack Router instance for navigation tracking
 */
// biome-ignore lint/suspicious/noExplicitAny: Router types are complex and vary by configuration
export function initSentry(router: any) {
	// Skip initialization if no DSN configured (local development)
	if (!env.VITE_SENTRY_DSN) {
		console.info("[Sentry] Disabled - no DSN configured");
		return;
	}

	Sentry.init({
		dsn: env.VITE_SENTRY_DSN,
		environment: env.VITE_APP_ENVIRONMENT,
		release: `frontend-app@${env.VITE_APP_VERSION}`,

		// Do not send PII (IP, headers, cookies) — LGPD: telemetry must not
		// carry personal data. User attribution is by opaque id only.
		sendDefaultPii: false,
		enableLogs: true,
		// React Query throws `CancelledError` on its own observer-unmount path —
		// it's an internal cleanup signal, not a real failure. With the console
		// integration capturing every error log, prod was paying for one Sentry
		// event per route navigation.
		ignoreErrors: ["CancelledError", /^CancelledError(:|$)/],
		integrations: [
			Sentry.consoleLoggingIntegration({ levels: ["error"] }),
			// TanStack Router integration for automatic pageload/navigation tracing
			Sentry.tanstackRouterBrowserTracingIntegration(router),

			// Session Replay for visual debugging of user sessions.
			// Fully masked (LGPD): no unmasked text or media may reach the
			// replay — masked replay carries no personal data, so no consent
			// gate is needed. Do not flip these to false.
			Sentry.replayIntegration({
				// Mask all text content so no PII (CPF, salary, names) is captured
				maskAllText: true,
				// Block media elements (uploaded worker/animal photos) from replay
				blockAllMedia: true,
			}),
		],

		// Performance monitoring - sample rate for transactions
		tracesSampleRate: env.VITE_SENTRY_TRACES_SAMPLE_RATE,

		// Propagate traces to our backend API for distributed tracing
		tracePropagationTargets: [
			"localhost",
			/^https:\/\/backend-ecs\.(stage\.)?app\.ruralops\.com\.br/,
		],

		// Session Replay sampling rates
		replaysSessionSampleRate: env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
		replaysOnErrorSampleRate: env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,

		// Filter or sanitize events before sending
		beforeSend(event) {
			// You can modify or filter events here
			// Return null to drop the event
			return event;
		},
	});

	console.info(
		`[Sentry] Initialized for ${env.VITE_APP_ENVIRONMENT} environment`,
	);
}

/**
 * Set user context in Sentry for better error attribution.
 * Call this after user authentication.
 */
export function setSentryUser(
	user: {
		id: string;
	} | null,
) {
	if (user) {
		// Opaque id only — no email/username. LGPD: telemetry must not carry PII.
		Sentry.setUser({
			id: user.id,
		});
	} else {
		Sentry.setUser(null);
	}
}

/**
 * Publish the service-worker update state so a stale cohort is visible in
 * Sentry — "how many sessions are sitting on a deferred update, and why".
 *
 * The value is the closed `UpdateState` union and nothing else: no free text,
 * no interpolation, and nothing derived from a form. The update layer reads a
 * queue depth and a dirtiness enum, never a field value, so LGPD-sensitive
 * content (CPF, salary) cannot reach telemetry through this tag.
 *
 * Typed by a type-only import, so this module gains no runtime dependency on
 * the PWA layer that already depends on it.
 */
export function setSentryUpdateState(state: UpdateState) {
	Sentry.setTag("sw.update.state", state);
}

/**
 * Capture an exception manually with additional context.
 */
export function captureError(
	error: Error,
	context?: {
		tags?: Record<string, string>;
		extra?: Record<string, unknown>;
	},
) {
	Sentry.captureException(error, {
		tags: context?.tags,
		extra: context?.extra,
	});
}

// Re-export Sentry for direct access when needed
export { Sentry };
